import {
  contactKeys,
  type EditReceiptExtractionInput,
  type ReceiptCategorySuggestion,
  type ReceiptScanJob,
  receiptScanKeys,
} from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, onUnmounted, ref, watch } from 'vue'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { useApi } from '@/app/api'
import { problemCode, problemDetail } from '../internal/problem.ts'

const POLL_INTERVAL_MS = 2000

export interface UseReceiptScanOptions {
  readonly ledgerId: () => string | undefined
  readonly jobId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseReceiptScan {
  readonly job: ComputedRef<ReceiptScanJob | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly retrying: ComputedRef<boolean>
  /** An object URL for the stored photo - `undefined` until it loads, revoked on cleanup. */
  readonly imageUrl: ComputedRef<string | undefined>
  /** Suggested categories for lines extraction left uncategorised - read-only; the review form
   * decides whether and how to prefill a line from one. */
  readonly suggestions: ComputedRef<readonly ReceiptCategorySuggestion[]>
  /** Accounts backing the ledger's contacts: a line posted to one was bought by that contact. */
  readonly contactAccountIds: ComputedRef<ReadonlySet<string>>
  readonly isSaving: ComputedRef<boolean>
  readonly saveErrorDetail: ComputedRef<string | undefined>
  readonly isRescanning: ComputedRef<boolean>
  readonly isCommitting: ComputedRef<boolean>
  readonly commitErrorDetail: ComputedRef<string | undefined>
  readonly commitErrorCode: ComputedRef<string | undefined>
  retry(): void
  save(input: EditReceiptExtractionInput): Promise<ReceiptScanJob>
  rescan(): Promise<ReceiptScanJob>
  commit(): Promise<ReceiptScanJob>
}

export function useReceiptScan(options: UseReceiptScanOptions): UseReceiptScan {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.jobId() !== undefined,
  )

  const jobQuery = useQuery({
    queryKey: computed(() => receiptScanKeys.detail(options.ledgerId() ?? '', options.jobId() ?? '')),
    queryFn: ({ signal }) => api.receiptScans.get(options.ledgerId()!, options.jobId()!, signal),
    enabled: active,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? POLL_INTERVAL_MS : false),
  })

  const imageActive = computed(() => active.value && jobQuery.data.value !== undefined)
  const imageQuery = useQuery({
    queryKey: computed(() => [
      ...receiptScanKeys.detail(options.ledgerId() ?? '', options.jobId() ?? ''),
      'image',
    ]),
    queryFn: ({ signal }) => api.receiptScans.image(options.ledgerId()!, options.jobId()!, signal),
    enabled: imageActive,
    retry: false,
    staleTime: Infinity,
  })

  const imageUrl = ref<string | undefined>(undefined)
  watch(
    () => imageQuery.data.value,
    (download, _previous, onCleanup) => {
      if (download === undefined) {
        imageUrl.value = undefined
        return
      }
      const url = URL.createObjectURL(download.blob)
      imageUrl.value = url
      onCleanup(() => URL.revokeObjectURL(url))
    },
  )
  onUnmounted(() => {
    if (imageUrl.value !== undefined) URL.revokeObjectURL(imageUrl.value)
  })

  const suggestionsActive = computed(
    () =>
      active.value &&
      (jobQuery.data.value?.status === 'READY' || jobQuery.data.value?.status === 'NEEDS_REVIEW'),
  )
  const suggestionsQuery = useQuery({
    queryKey: computed(() => [
      ...receiptScanKeys.detail(options.ledgerId() ?? '', options.jobId() ?? ''),
      'category-suggestions',
    ]),
    queryFn: ({ signal }) =>
      api.receiptScans.categorySuggestions(options.ledgerId()!, options.jobId()!, signal),
    enabled: suggestionsActive,
    retry: false,
  })

  const contactsQuery = useQuery({
    queryKey: computed(() => contactKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.contacts.list(options.ledgerId()!, signal),
    enabled: active,
  })

  function invalidate(): Promise<void> {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return Promise.resolve()
    return Promise.all(
      receiptScanKeys
        .invalidatedByWrite(ledgerId)
        .map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const saveMutation = useMutation({
    mutationFn: (input: EditReceiptExtractionInput) =>
      api.receiptScans.editExtraction(options.ledgerId()!, options.jobId()!, input),
    onSuccess: () => invalidate(),
  })

  const rescanMutation = useMutation({
    mutationFn: () => api.receiptScans.rescan(options.ledgerId()!, options.jobId()!),
    onSuccess: () => invalidate(),
  })

  const commitMutation = useMutation({
    mutationFn: () => api.receiptScans.commit(options.ledgerId()!, options.jobId()!),
    onSuccess: () => invalidate(),
  })

  return {
    job: computed(() => jobQuery.data.value),
    isLoading: computed(() => active.value && jobQuery.isPending.value),
    error: computed(() => jobQuery.error.value),
    notFound: computed(() => isNotFound(jobQuery.error.value)),
    retrying: retrying(jobQuery),
    imageUrl: computed(() => imageUrl.value),
    suggestions: computed(() => suggestionsQuery.data.value ?? []),
    contactAccountIds: computed(
      () => new Set((contactsQuery.data.value ?? []).map((contact) => contact.accountId)),
    ),
    isSaving: computed(() => saveMutation.isPending.value),
    saveErrorDetail: computed(() => problemDetail(saveMutation.error.value)),
    isRescanning: computed(() => rescanMutation.isPending.value),
    isCommitting: computed(() => commitMutation.isPending.value),
    commitErrorDetail: computed(() => problemDetail(commitMutation.error.value)),
    commitErrorCode: computed(() => problemCode(commitMutation.error.value)),
    retry: () => {
      void jobQuery.refetch()
    },
    save: (input) => saveMutation.mutateAsync(input),
    rescan: () => rescanMutation.mutateAsync(),
    commit: () => commitMutation.mutateAsync(),
  }
}
